import { css } from '@bts-web/utils-style-engine';
import { interpolateTranslation, Locale } from '@bts-web/utils-lokalise';
import {
  getRoundedPrecision,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import { convertToLocaleDateString } from '@bts-web/utils-formatting-client';
import {
  AssetImage,
  ButtonBase,
  HyperLinkButtonBase,
  Status,
} from '../../common';
import {
  SavingsPlanItemType,
  SavingsPlansListTranslationsKeys,
} from '../types';
import { useIsBelowScreenWidth } from '../../layouts/utils/useIsBelowScreenWidth';
import { SavingsPlanDetailsDrawer } from '../savings-plan-details/SavingsPlanDetailsDrawer/SavingsPlanDetailsDrawer';
import { useModalDrawerContext } from '../../common/components/ModalDrawerContext/ModalDrawerContext';
import { getAppConfig } from '@bts-web/core-app-config';
import { SavingsPlanFrequency } from '@bts-web/data-layer/server';

export interface SavingsPlanItemProps {
  savingsPlan: SavingsPlanItemType;
  applicationLocale: Locale;
  translations: Pick<
    SavingsPlansListTranslationsKeys,
    | 'savingsPlansListMonthly'
    | 'savingsPlansListMonthlyNextOn'
    | 'savingsPlansListWeeklyNextOn'
    | 'savingsPlansListBiweeklyNextOn'
    | 'savingsPlansListDailyNextOn'
    | 'savingsPlansListActive'
    | 'savingsPlansListCancelled'
    | 'savingsPlansListEmpty'
    | 'monthly'
    | 'weekly'
    | 'biweekly'
    | 'daily'
  >;
}

const getNextOnTranslation = (
  frequency: SavingsPlanFrequency,
  translations: Pick<
    SavingsPlansListTranslationsKeys,
    | 'savingsPlansListMonthlyNextOn'
    | 'savingsPlansListWeeklyNextOn'
    | 'savingsPlansListBiweeklyNextOn'
    | 'savingsPlansListDailyNextOn'
  >,
) => {
  if (frequency === 'DAILY') return translations.savingsPlansListDailyNextOn;

  if (frequency === 'WEEKLY') return translations.savingsPlansListWeeklyNextOn;

  if (frequency === 'BIWEEKLY')
    return translations.savingsPlansListBiweeklyNextOn;

  return translations.savingsPlansListMonthlyNextOn;
};

const getFrequencyTranslation = (
  frequency: SavingsPlanFrequency,
  translations: Pick<
    SavingsPlansListTranslationsKeys,
    'monthly' | 'weekly' | 'biweekly' | 'daily'
  >,
) => {
  if (frequency === 'DAILY') return translations.daily;

  if (frequency === 'WEEKLY') return translations.weekly;

  if (frequency === 'BIWEEKLY') return translations.biweekly;

  return translations.monthly;
};

const SavingsPlanItem = ({
  savingsPlan,
  applicationLocale,
  translations,
}: SavingsPlanItemProps) => {
  const { openDrawer } = useModalDrawerContext();

  const isMobile = useIsBelowScreenWidth(1024);

  const formattedDate = convertToLocaleDateString(
    savingsPlan?.nextRecurrence,
    applicationLocale,
  );

  const { currency } = getAppConfig();

  const formattedValue = intlFiatValueFormatting(
    Number(getRoundedPrecision(savingsPlan?.fiatAmount)),
    {
      locale: applicationLocale,
      fractionDigits: savingsPlan?.fiatAmount?.precision,
      currency,
    },
  );

  const savingsPlanContent = (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        borderBottomColor: 'neutrals.divider',
        borderBottomWidth: '1px',
        gap: 'small',
        padding: 'medium',
        width: '100%',
      })}
      key={savingsPlan?.id}
      data-testid={`savings-plan-item-${savingsPlan?.id}`}
    >
      <AssetImage
        height={24}
        width={24}
        src={savingsPlan?.asset?.logoUrl as string}
        alt={savingsPlan?.asset?.name as string}
        placeholder="empty"
      />

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 'extra_small_4',
        })}
      >
        <span
          className={css({
            color:
              savingsPlan.status === 'CANCELED'
                ? 'neutrals.text_disabled'
                : 'neutrals.text_primary',
          })}
        >
          {savingsPlan?.name}
        </span>

        <span
          className={css({
            color: 'neutrals.text_secondary',
            fontSize: 'body.medium',
            fontWeight: 'body.medium',
            letterSpacing: 'body.medium',
            lineHeight: 'body.medium',
          })}
        >
          {savingsPlan?.status === 'ACTIVE'
            ? interpolateTranslation(
                getNextOnTranslation(
                  savingsPlan?.frequency ??
                    ('MONTHLY' satisfies SavingsPlanFrequency),
                  translations,
                ),
                {
                  date: formattedDate,
                },
              )
            : getFrequencyTranslation(
                savingsPlan?.frequency ?? 'MONTHLY',
                translations,
              )}
        </span>
      </div>

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'extra_small_4',
          alignItems: 'end',
          marginInlineStart: 'auto',
        })}
      >
        <span
          className={css({
            color:
              savingsPlan.status === 'CANCELED'
                ? 'neutrals.text_disabled'
                : 'neutrals.text_primary',
            fontSize: 'label.medium_medium',
            lineHeight: 'label.medium_medium',
            letterSpacing: 'label.medium_medium',
            fontWeight: 'label.medium_medium',
          })}
        >
          {formattedValue}
        </span>

        <Status
          size="small"
          visual={
            savingsPlan?.status === 'ACTIVE' ? 'positive' : 'filledNegative'
          }
          text={
            savingsPlan?.status === 'ACTIVE'
              ? translations.savingsPlansListActive
              : translations.savingsPlansListCancelled
          }
        />
      </div>
    </div>
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    e.stopPropagation();

    if (savingsPlan?.id && !isMobile) {
      openDrawer(SavingsPlanDetailsDrawer, {
        savingsPlanId: savingsPlan.id,
      });
    }
  };

  return isMobile ? (
    <HyperLinkButtonBase
      visual="ghost"
      noPadding
      to={`/savings-plans/${savingsPlan?.id}`}
    >
      {savingsPlanContent}
    </HyperLinkButtonBase>
  ) : (
    <ButtonBase
      fullWidth
      noPadding
      visual="ghost"
      type="button"
      aria-label="view savings plan details"
      onClick={handleClick}
    >
      {savingsPlanContent}
    </ButtonBase>
  );
};

export { SavingsPlanItem };
